import styled from '@emotion/styled';
import { DeviceStateFlag, LeaderboardHeaderContent } from '@racemap/utilities/consts/events';
import { formatTimeDuration } from '@racemap/utilities/formattings/times';
import { formatDistance } from '@racemap/utilities/formattings/units';
import {
  type Geofence,
  type PreparedStarterInfo,
  sortTimes,
  testEventHasVirtDuration,
} from '@racemap/utilities/functions/timing';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import type { Splits } from '@racemap/utilities/types/types';
import { Unit } from '@racemap/utilities/types/units';
import classNames from 'classnames';
import type { Immutable } from 'immer';
import type { FC, ImgHTMLAttributes } from 'react';
import { ICON_CDN_PATH } from '../../../BasicComponents/IconChooser/IconChooser';
import type { Column } from '../TableBody';
import { Cell } from './BasicCell';

export const LARGE_TIME_COL_WIDTH = 150;
export const SMALL_TIME_COL_WIDTH = 130;

interface Props {
  headerContent: Immutable<Array<LeaderboardHeaderContent>>;
  name?: string;
  icon?: string;
  distance?: string;
}

export const TimesColumHeaderTitle: FC<Props> = ({ headerContent, name, icon, distance }) => {
  return (
    <>
      {icon != null && headerContent.includes(LeaderboardHeaderContent.ICON) && (
        <Icon>
          <MapIcon src={icon} />
        </Icon>
      )}
      <Title>
        {name != null && headerContent.includes(LeaderboardHeaderContent.NAME) && (
          <Name title={name}>{name}</Name>
        )}
        {distance != null && headerContent.includes(LeaderboardHeaderContent.DISTANCE) && (
          <Distance>{distance}</Distance>
        )}
      </Title>
    </>
  );
};

const Distance = styled.div`
  font-size: 10px;
  opacity: 0.7;
`;

function TimesDeltaColComponent({
  starterInfo,
  event,
  index,
}: {
  starterInfo: Immutable<PreparedStarterInfo>;
  event: Immutable<RacemapEvent>;
  index: number;
}) {
  const unitType = event.playerOptions.unitType;
  const eventHasVirtDuration = testEventHasVirtDuration(event);
  const starterResults = starterInfo.results[index];

  return (
    <Cell
      title={
        eventHasVirtDuration
          ? formatDistance({
              value: starterResults?.relativeProgress ?? 0,
              inputUnit: Unit.Distance.m,
              unitType,
            })
          : formatTimeDuration(starterResults?.relativeProgress)
      }
      style={{
        opacity: starterInfo.state === DeviceStateFlag.DISQUALIFIED ? 0.7 : 1,
        fontStyle: starterInfo.state === DeviceStateFlag.DISQUALIFIED ? 'italic' : 'normal',
      }}
    >
      {eventHasVirtDuration
        ? formatDistance({
            value: starterResults?.relativeDeltaProgress ?? 0,
            inputUnit: Unit.Distance.m,
            unitType,
          })
        : formatTimeDuration(starterResults?.relativeDeltaProgress)}
    </Cell>
  );
}

function TimesColComponent({
  starterInfo,
  event,
  index,
}: {
  starterInfo: Immutable<PreparedStarterInfo>;
  event: Immutable<RacemapEvent>;
  index: number;
}) {
  const unitType = event.playerOptions.unitType;
  const eventHasVirtDuration = testEventHasVirtDuration(event);
  const starterResults = starterInfo.results[index];

  return (
    <Cell
      title={
        eventHasVirtDuration
          ? `Delta distance: ${formatDistance({
              value: starterResults?.relativeDeltaProgress ?? 0,
              inputUnit: Unit.Distance.m,
              unitType,
            })}`
          : `Delta time: ${formatTimeDuration(starterResults?.relativeDeltaProgress)}`
      }
      style={{
        opacity: starterInfo.state === DeviceStateFlag.DISQUALIFIED ? 0.7 : 1,
        fontStyle: starterInfo.state === DeviceStateFlag.DISQUALIFIED ? 'italic' : 'normal',
      }}
    >
      {eventHasVirtDuration
        ? formatDistance({
            value: starterResults?.relativeProgress ?? 0,
            inputUnit: Unit.Distance.m,
            unitType,
          })
        : formatTimeDuration(starterResults?.relativeProgress)}
    </Cell>
  );
}

function getDeltaTimesSorter(index: number, eventHasVirtDuration: boolean) {
  return (a: Immutable<PreparedStarterInfo>, b: Immutable<PreparedStarterInfo>) => {
    const aDelPr = a?.results[index]?.relativeDeltaProgress;
    const bDelPr = b?.results[index]?.relativeDeltaProgress;

    if (eventHasVirtDuration) {
      if (aDelPr == null) return -1;
      if (bDelPr == null) return 1;
      return bDelPr - aDelPr;
    }
    return sortTimes(aDelPr, bDelPr);
  };
}

function getTimesSorter(index: number, eventHasVirtDuration: boolean) {
  return (a: Immutable<PreparedStarterInfo>, b: Immutable<PreparedStarterInfo>) =>
    eventHasVirtDuration
      ? b?.results[index]?.relativeProgress - a?.results[index]?.relativeProgress
      : sortTimes(a?.results[index]?.relativeProgress, b?.results[index]?.relativeProgress);
}

export function getTimesCol(
  event: Immutable<RacemapEvent>,
  index: number,
  timekeepings: Immutable<Array<Geofence>>,
  splits: Immutable<Splits>,
  windowWidth: number,
  isDeltaCol: boolean,
): Column {
  const unitType = event.playerOptions.unitType;
  const eventHasVirtDuration = testEventHasVirtDuration(event);
  const visibleElements = event.modules.timing.visibleElements;
  const geofence = timekeepings[index];
  const splitIconName = splits?.find((split) => split.id === geofence.id)?.properties.icon;
  const name: string = splits == null ? `Split ${index}` : geofence.name;
  const icon = splitIconName;
  const distance = eventHasVirtDuration
    ? formatTimeDuration(geofence.timeOffset)
    : formatDistance({
        value: geofence.offset ?? 0,
        inputUnit: Unit.Distance.m,
        unitType,
      });
  return {
    key: isDeltaCol ? `times_delta_${geofence.id}` : `times_${geofence.id}`,
    title: (
      <TimesColumHeaderTitle
        headerContent={visibleElements.headerContent}
        name={name}
        icon={icon != null ? icon : `${ICON_CDN_PATH}/split-times_tickless.svg`}
        distance={distance}
      />
    ),
    width:
      windowWidth < 990 || timekeepings.length > 6 ? SMALL_TIME_COL_WIDTH : LARGE_TIME_COL_WIDTH,
    render: function renderer(row: Immutable<PreparedStarterInfo>) {
      return isDeltaCol ? (
        <TimesDeltaColComponent starterInfo={row} event={event} index={index} />
      ) : (
        <TimesColComponent starterInfo={row} event={event} index={index} />
      );
    },
    sorter: isDeltaCol
      ? getDeltaTimesSorter(index, eventHasVirtDuration)
      : getTimesSorter(index, eventHasVirtDuration),
  };
}

const Icon = styled.div`
  height: 30px;
  min-width: 30px;
  margin-right: 4px;
  display: flex;
  align-items: flex-start;
  justify-content: center;

  .icon-svg {
    height: 100%;
    width: 100%;
  }
`;

const Title = styled.div`
  width: 90%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const Name = styled.div`
  width: 85%;
  font-weight: 600;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

type MapIconProps = ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  className?: string;
};

const MapIcon = ({ src, className, ...props }: MapIconProps) => {
  return (
    <img
      className={classNames('icon', 'icon-svg', className)}
      src={src}
      {...props}
      alt="Map Icon"
    />
  );
};
